import { Schema } from '@pdfme/common';
import {
  type FormRole,
  zeroIndexedPage,
  type FieldType,
  type FormField,
  type RawFormFillerData,
  type TandemFormProperties,
  FormAnswers,
  makeFormAnswers,
  makeRawFormFillerData,
} from 'shared-utils/types/formFiller';
import { LoadedInitialData } from '../components/FormFiller/FormFillerProvider';

export const LETTER_WIDTH_PX = 612;

const LETTER_WIDTH_MM = 215.9;
const LETTER_HEIGHT_MM = 279.4;

export const MAX_PDF_WIDTH_PX = 1000;

const RADIO_GROUP_PREFIX = 'radioGroup-';

export function convertCoordinatesToPercent(coordinates: { x: number; y: number; width: number; height: number }) {
  return {
    xPercent: (coordinates.x / LETTER_WIDTH_MM) * 100,
    yPercent: (coordinates.y / LETTER_HEIGHT_MM) * 100,
    widthPercent: (coordinates.width / LETTER_WIDTH_MM) * 100,
    heightPercent: (coordinates.height / LETTER_HEIGHT_MM) * 100,
  };
}

export function convertSchemasToFormFields(
  schemas: Array<Array<Schema & { tandem: TandemFormProperties }>>,
  patientAnswers: FormAnswers,
  prescriberAnswers: FormAnswers,
  role: FormRole,
) {
  const formFields: Array<FormField> = [];
  const radioFields: Record<string, { group: string; optionValue: string }> = {};
  schemas.forEach((fields, index) => {
    for (const field of fields) {
      const isCurrentRole = field.tandem.role === role;
      const answer = field.tandem.role === 'Patient' ? patientAnswers[field.name] : prescriberAnswers[field.name];

      formFields.push({
        type: field.type === 'radioGroup' ? 'radio' : (field.type as FieldType),
        id: field.name,
        name: field.name,
        group: field.type === 'radioGroup' ? RADIO_GROUP_PREFIX + field.group : undefined,
        value: field.type === 'radioGroup' ? (field as any).tandem.pandadocOptionValue : undefined,
        autoFilledDate: field.type === 'date' ? field.tandem.autoFilledDate : undefined,
        isReadOnly: !isCurrentRole,
        readOnlyValue: !isCurrentRole ? answer : undefined,
        role: field.tandem.role, // Add role information for tooltips
        coordinates: {
          x: field.position.x,
          y: field.position.y,
          width: field.width,
          height: field.height,
          page: zeroIndexedPage(index),
        },
        required: isCurrentRole && (field as any).tandem.required,
      });

      if (field.type === 'radioGroup') {
        radioFields[field.name] = {
          group: (field as any).group,
          optionValue: (field as any).tandem.pandadocOptionValue,
        };
      }
    }
  });

  return { formFields, radioFields };
}

export function getInitialFieldsAndAnswers(
  formInfo: { schemas: Array<Array<Schema & { tandem: TandemFormProperties }>> },
  patientAnswers: FormAnswers,
  prescriberAnswers: FormAnswers,
  role: FormRole,
) {
  const { formFields, radioFields } = convertSchemasToFormFields(
    formInfo.schemas,
    patientAnswers,
    prescriberAnswers,
    role,
  );

  const initialAnswers: RawFormFillerData = makeRawFormFillerData({});
  const relevantAnswers = role === 'Patient' ? patientAnswers : prescriberAnswers;

  for (const [fieldId, answer] of Object.entries(relevantAnswers)) {
    if (radioFields[fieldId]) {
      const radioGroupKey = `${RADIO_GROUP_PREFIX}${radioFields[fieldId].group}`;
      if (answer === true) {
        initialAnswers[radioGroupKey] = radioFields[fieldId].optionValue;
      }
    } else {
      if (answer === null) {
        continue;
      }
      initialAnswers[fieldId] = answer;
    }
  }

  return { formFields, initialAnswers };
}

export function getValidValue(value: string | null | undefined | boolean) {
  if (value === null || value === undefined || value === '' || value === '{}') {
    return null;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return value.toString();
}

export function getSignedFields(args: { formFields: FormField[]; data: RawFormFillerData; role: FormRole }): string[] {
  const { formFields, data, role } = args;
  const signedFields = new Set<string>();
  for (const field of formFields) {
    if (field.type === 'signature') {
      if (field.role !== role) {
        continue;
      }
      const value = data[field.id];
      if (value !== null && value !== undefined) {
        signedFields.add(field.id);
      }
    }
  }
  return Array.from(signedFields);
}

export function convertFormDataToAnswers(args: { data: RawFormFillerData; formFields: FormField[] }): {
  data: FormAnswers;
} {
  const idToFieldMap = new Map<string, FormField>();
  for (const field of args.formFields) {
    if (!field.isReadOnly) {
      idToFieldMap.set(field.id, field);
    }
  }
  const { data } = args;
  const filteredData: FormAnswers = makeFormAnswers({});

  for (const [key, value] of Object.entries(data)) {
    // Handle regular fields
    if (idToFieldMap.has(key)) {
      const validValue = getValidValue(value as string);
      const field = idToFieldMap.get(key);
      if (field?.type === 'date' && field.autoFilledDate) {
        // Skip auto-filled dates since they should never be set by the client
        continue;
      }
      filteredData[key] = validValue;
    } else if (key.startsWith(RADIO_GROUP_PREFIX)) {
      /**
       * Radio groups get saved as radioGroup-<group name> -> value
       *
       * Example:
       *
       * In the pdfme template schema:
       * {
       *   type: "radioGroup",
       *   name: "radio-groupA-option1",
       *   group: "groupA",
       *   tandem: {
       *     pandadocOptionValue: "option1",
       *   },
       * }
       *
       * As a FormField:
       * {
       *  id: "radio-groupA-option1",
       *  group: "radioGroup-groupA",
       *  value: "option1"
       * }
       *
       * As a `react-hook-form` answer:
       * { "radioGroup-groupA": "option1" }
       *
       * As a `/set-form-answers` request:
       * { "radio-groupA-option1": "True" }
       */
      const radioFieldsInGroup = args.formFields.filter(
        (field) => !field.isReadOnly && field.type === 'radio' && field.group === key,
      );
      const selected = radioFieldsInGroup.find((field) => field.value === value)?.id;
      if (selected) {
        filteredData[selected] = getValidValue(true);
      }
    }
  }

  return { data: filteredData };
}

// Helper function to determine if a field value is empty
// Uses the same logic as getValidValue from formFiller.ts
export function isFieldEmpty(value: string | null | undefined | boolean): boolean {
  if (value === null || value === undefined || value === '' || value === '{}') {
    return true;
  }
  return false;
}

export type FieldState = {
  isAssigned: boolean;
  isRequired: boolean;
  isEmpty: boolean;
  isReadOnly: boolean;
  role: FormRole;
  id: string;
};

// Helper function to determine field states relevant for styling
export function getFieldState(field: FormField, value: any): FieldState {
  const isAssigned = !field.isReadOnly;
  const isRequired = !!field.required;
  const isEmpty = isFieldEmpty(value);
  const isReadOnly = !isAssigned;

  return {
    isAssigned,
    isRequired,
    isEmpty,
    isReadOnly,
    role: field.role,
    id: field.id,
  };
}

// Helper functions for form navigation and completion tracking
function getRequiredFields(args: {
  initialData: LoadedInitialData;
  formAnswers: RawFormFillerData;
  role: FormRole;
}): FormField[] {
  const hiddenFields = getHiddenConditionalFields(args.initialData, args.formAnswers);

  const requiredFields = [];
  for (const field of args.initialData.formFields) {
    if (field.isReadOnly || field.autoFilledDate || !field.required || field.role !== args.role) {
      // Filter out things that aren't actually required or assigned to this role
      continue;
    }
    if (hiddenFields.has(field.id)) {
      continue;
    }
    requiredFields.push(field);
  }
  return requiredFields;
}

function getRequiredEmptyFields(requiredFields: FormField[], rawFormAnswers: RawFormFillerData): FormField[] {
  const seenGroups = new Set<string>();
  return requiredFields.filter((field) => {
    switch (field.type) {
      case 'radio':
        if (field.group) {
          if (seenGroups.has(field.group)) {
            return false;
          }
          seenGroups.add(field.group);
          // For radio buttons, check if the group has a selected value
          const groupValue = rawFormAnswers[field.group];
          return isFieldEmpty(groupValue);
        }
        return false;
      case 'date':
        if (field.autoFilledDate) {
          return false;
        } else {
          const value = rawFormAnswers[field.id];
          return isFieldEmpty(value);
        }
      case 'checkbox': {
        const value = rawFormAnswers[field.id];
        return value !== true;
      }
      case 'signature':
      case 'text': {
        const value = rawFormAnswers[field.id];
        return isFieldEmpty(value);
      }
      default: {
        const _typecheck: never = field.type;
        console.error('Unknown field type', _typecheck);
        break;
      }
    }
  });
}

export interface FormCompletionStats {
  totalRequired: number;
  filledRequired: number;
  remainingRequired: number;
  isComplete: boolean;
}

export function getFormCompletionStats(args: {
  initialData: LoadedInitialData;
  rawFormData: RawFormFillerData;
  role: FormRole;
}): FormCompletionStats {
  const requiredFields = getRequiredFields({
    initialData: args.initialData,
    role: args.role,
    formAnswers: args.rawFormData,
  });
  const requiredEmptyFields = getRequiredEmptyFields(requiredFields, args.rawFormData);

  return {
    totalRequired: requiredFields.length,
    filledRequired: requiredFields.length - requiredEmptyFields.length,
    remainingRequired: requiredEmptyFields.length,
    isComplete: requiredEmptyFields.length === 0,
  };
}

export function getNextRequiredEmptyField(args: {
  initialData: LoadedInitialData;
  rawFormData: RawFormFillerData;
  currentFieldId: string | null;
  role: FormRole;
}): FormField | null {
  const requiredFields = getRequiredFields({
    initialData: args.initialData,
    role: args.role,
    formAnswers: args.rawFormData,
  });
  const requiredEmptyFields = getRequiredEmptyFields(requiredFields, args.rawFormData);

  if (requiredEmptyFields.length === 0) {
    return null;
  }

  if (!args.currentFieldId) {
    return requiredEmptyFields[0];
  }

  // Find current field index and return next field
  const currentIndex = requiredEmptyFields.findIndex((field) => field.id === args.currentFieldId);
  if (currentIndex === -1 || currentIndex >= requiredEmptyFields.length - 1) {
    return requiredEmptyFields[0]; // Return to first if not found or at end
  }

  return requiredEmptyFields[currentIndex + 1];
}

export function getHiddenConditionalFields(initialData: LoadedInitialData, formData: RawFormFillerData): Set<string> {
  const { data: formAnswers } = convertFormDataToAnswers({
    formFields: initialData.formFields,
    data: formData,
  });
  // Collect all fields involved in conditions, since they should be hidden by default
  const hiddenFields = new Set<string>();
  for (const conditionalField of initialData.conditionalFields) {
    for (const fieldId of conditionalField.showFieldsWhenFieldHasValue) {
      hiddenFields.add(fieldId);
    }
  }
  // For any conditions that are met, remove the fields to show from the set
  for (const conditionalField of initialData.conditionalFields) {
    if (formAnswers[conditionalField.fieldId] === true) {
      for (const fieldId of conditionalField.showFieldsWhenFieldHasValue) {
        hiddenFields.delete(fieldId);
      }
    }
  }
  return hiddenFields;
}

export function getTabIndex(fieldState: FieldState) {
  // Assigned fields get tabIndex=1 (navigated first)
  // Unassigned fields get tabIndex=0 (navigated after anything with positive tabIndex)
  // This ensures the fields assigned to the signer are navigated first.
  return fieldState.isAssigned ? 1 : 0;
}

export function getCompletedDocumentUrl(token: string, attachment: boolean) {
  const queryString = attachment ? '?attachment=true' : '';
  return `${import.meta.env.VITE_API_HOST}/forms/${token}/completed-document-pdf${queryString}`;
}

export function getBasePdfDownloadUrl(token: string) {
  return `${import.meta.env.VITE_API_HOST}/forms/${token}/base-pdf?attachment=true`;
}
